import { listSchoolOrganizationsAll } from 'util/listSchoolOrganizationsAll';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import { Button, Modal, ModalProps, Select, Switch } from 'components/ui';
import {
  EditUserAdminForm,
  editUserAdminFormSchema,
} from 'forms/admin/edit-user.from';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import useSWR from 'swr';
import {
  UserSummary,
  useUsersApi,
  useEventsApi,
  useSchoolsApi,
  OrganizationSummary,
} from 'services/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApproverPermissionsCategoriesResponse } from 'types/event-setup';
import { RoleMap } from 'hooks/useGetRole';
import { ChevronRight } from '@mui/icons-material';
import { SelectionContent } from './SelectionContent';

export interface EditUserModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {
  userId: string;
  schoolId?: string;
  isMultiStepApprovalEnabled: boolean;
  onSaved?: (info: {
    rolesBefore: string[];
    rolesAfter: string[];
    changedCount: number;
  }) => void;
  onDismissed?: (info: { hadChanges: boolean }) => void;
}

type SelectionModalType = 'organizations' | 'approval-categories' | null;

export const EditUserModal = ({
  open,
  userId,
  schoolId,
  onClose,
  isMultiStepApprovalEnabled,
  onSaved,
  onDismissed,
}: EditUserModalProps): ReactElement => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditUserAdminForm>({
    resolver: zodResolver(editUserAdminFormSchema),
  });

  useEffect(() => {
    if (Object.keys(errors).length > 0)
      enqueueSnackbar('There are validation errors in the form.', { variant: 'error' });

  }, [errors]);

  const [isSubmitting, setSubmitting] = useState(false);
  const [showSelectionContent, setShowSelectionContent] = useState(false);
  const [selectionModalType, setSelectionModalType] =
    useState<SelectionModalType>(null);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>(
    [],
  );
  const [selectedApprovalCategories, setSelectedApprovalCategories] = useState<
    string[]
  >([]);

  const { enqueueSnackbar } = useSnackbar();
  const initialSnapshotRef = useRef<{ roles: string[]; deactivated: boolean } | null>(null);

  const usersApi = useUsersApi();
  const eventsApi = useEventsApi();
  const schoolsApi = useSchoolsApi(); // Add schoolsApi hook

  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [isOrgsLoading, setIsOrgsLoading] = useState(false);

  // Fetch organizations once when the modal is opened
  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!schoolId) return;
      setIsOrgsLoading(true);
      try {
        const allOrganizations = await listSchoolOrganizationsAll(
          schoolsApi,
          schoolId,
        );
        setOrganizations(allOrganizations);
      } catch (error) {
        enqueueSnackbar('An unexpected error occurred while loading organizations', {
          variant: 'error',
        });
      } finally {
        setIsOrgsLoading(false);
      }
    };

    // Fetch only if modal is open and organizations haven't been loaded yet.
    if (open && schoolId && organizations.length === 0)
      void fetchOrganizations();

  }, [open, schoolId, organizations.length, schoolsApi, enqueueSnackbar]);


  // Fetch event setup categories for the school
  const { data: eventSetupData } =
    useSWR<ApproverPermissionsCategoriesResponse>(
      schoolId
        ? [eventsApi.getSchoolEventSetupCategoriesApproverPermissions]
        : null,
      async () => {
        const result =
          await eventsApi.getSchoolEventSetupCategoriesApproverPermissions(
            schoolId,
          );
        if (result.success) return result.value;
        throw new Error(result.error.message);
      },
    );

  const onSubmit = throttle(
    async (data: EditUserAdminForm) => {
      setSubmitting(true);
      try {
        // Validation checks...
        if (!isMultiStepApprovalEnabled && data.role.includes('approver')) {
          enqueueSnackbar('Multi-step approval is not enabled for this school', {
            variant: 'error',
          });
          setSubmitting(false);
          return;
        }

        if (data.role.includes('admin') && selectedOrganizations.length === 0) {
          enqueueSnackbar('Admin role requires at least one organization to be selected', {
            variant: 'error',
          });
          setSubmitting(false);
          return;
        }
        if (data.role.includes('approver') && selectedApprovalCategories.length === 0) {
          enqueueSnackbar('Approver role requires at least one permission to be selected', {
            variant: 'error',
          });
          setSubmitting(false);
          return;
        }

        // Convert approver permissions to the new format for API
        let approverPermissions:
          | Array<{
              schoolEventSetupCategoryId: string;
            }>
          | undefined;

        // Only include approver permissions if approver role is selected
        if (
          data.role.includes('approver') &&
          selectedApprovalCategories.length > 0
        ) {
          approverPermissions = selectedApprovalCategories.map(categoryId => ({
            schoolEventSetupCategoryId: categoryId,
          }));
        }

        // Only include organization IDs if admin role is selected
        const organizationIds = data.role.includes('admin') ? selectedOrganizations : [];

        const result = await usersApi.updateUser(userId, {
          ...data,
          schoolId,
          organizationIds,
          approverPermissions: approverPermissions || undefined,
        });

        if (result.success) {
          enqueueSnackbar('User updated successfully', { variant: 'success' });

          const rolesBefore: string[] = initialSnapshotRef.current?.roles ?? [];
          const rolesAfter: string[] = data.role ?? [];
          const changedCount = new Set([
            ...rolesBefore.filter(role => !rolesAfter.includes(role)),
            ...rolesAfter.filter(role => !rolesBefore.includes(role)),
          ]).size;
          onSaved?.({ rolesBefore, rolesAfter, changedCount });

          onClose?.();
        } else {
          enqueueSnackbar(result.error.message, { variant: 'error' });
        }
      } catch (error) {
        enqueueSnackbar('An unexpected error occurred', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
    200,
    { leading: true },
  );

  // Handle role checkbox changes
  const handleRoleChange = (
    roleValue: 'admin' | 'executive-admin' | 'approver',
    checked: boolean,
  ) => {
    if (checked) {
      // Adding a role
      if (roleValue === 'executive-admin') {
        // Executive admin unchecks all other roles
        setValue('role', ['executive-admin']);
        // Don't clear selections immediately - handle in API call
      } else if (roleValue === 'admin') {
        // Admin can be combined with approver, but not with executive-admin
        setSelectionModalType('organizations');
        setShowSelectionContent(true);
      } else if (roleValue === 'approver') {
        // Approver can be combined with admin, but not with executive-admin
        setSelectionModalType('approval-categories');
        setShowSelectionContent(true);
      }
    } else if (roleValue === 'admin') {
      // Removing admin role - open selection modal to confirm removal
      setSelectionModalType('organizations');
      setShowSelectionContent(true);
    } else if (roleValue === 'approver') {
      // Removing approver role - open selection modal to confirm removal
      setSelectionModalType('approval-categories');
      setShowSelectionContent(true);
    } else if (roleValue === 'executive-admin') {
      // Removing executive-admin role - open selection modal to confirm removal
      setValue('role', []);
    }
  };

  // Handle box click to toggle role
  const handleBoxClick = (roleValue: 'admin' | 'executive-admin' | 'approver') => {
    const currentRoles = (watch('role') || []);
    const isCurrentlyChecked = currentRoles.includes(roleValue);
    handleRoleChange(roleValue, !isCurrentlyChecked);
  };

  // Check if a role should be disabled
  const isRoleDisabled = (
    roleValue: 'admin' | 'executive-admin' | 'approver',
  ): boolean => {
    if (roleValue === 'executive-admin') {
      // Executive admin is never disabled
      return false;
    }
    if (roleValue === 'admin') {
      // Admin is never disabled - clicking it will uncheck executive-admin
      return false;
    }
    if (roleValue === 'approver') {
      // Approver is only disabled if multi-step approval is not enabled
      return !isMultiStepApprovalEnabled;
    }

    return false;
  };

  const rUser = useSWR<UserSummary>(
    userId ? [usersApi.getById, userId, schoolId] : null,
  );

  useEffect(() => {
    if (!rUser.data || !eventSetupData) return;

    let { role } = rUser.data;
    const { isActive } = rUser.data;

    if (role.includes(RoleMap.user)) role = [];

    // Initialize selected organizations from adminOrganizations
    if (rUser.data.adminOrganizations)
      setSelectedOrganizations(rUser.data.adminOrganizations.map(org => org.id));


    // Transform the approver permissions from an array (API) to a record (form schema)
    const initialApproverPermissions: EditUserAdminForm['approverPermissions'] = {};
    if (
      rUser.data.approverPermissions &&
      Array.isArray(rUser.data.approverPermissions)
    ) {
      rUser.data.approverPermissions.forEach(
        (permission: { schoolEventSetupCategoryId: string }) => {
          const category = eventSetupData.categories.find(
            c => c.id === permission.schoolEventSetupCategoryId,
          );
          if (category) {
            initialApproverPermissions[permission.schoolEventSetupCategoryId] = {
              label: category.name,
              value: true,
            };
          }
        },
      );
      // Sync the separate state used by the selection modal
      setSelectedApprovalCategories(Object.keys(initialApproverPermissions));
    }

    reset({
      ...rUser.data,
      approverPermissions: initialApproverPermissions, // Use the transformed record
      role: role as unknown as EditUserAdminForm['role'],
      deactivated: !isActive,
    });

    initialSnapshotRef.current = { roles: role, deactivated: !isActive };
  }, [rUser.data, eventSetupData, reset]);

  const currentRoles = watch('role') || [];

  const handleSelectionApply = (selectedIds: string[]) => {
    const currentRoles = (watch('role') || []);

    if (selectionModalType === 'organizations') {
      setSelectedOrganizations(selectedIds);

      // If user made selections, add admin role; if no selections, remove admin role
      if (selectedIds.length > 0) {
        const newRoles = currentRoles.filter(role => role !== 'executive-admin');
        if (!currentRoles.includes('admin'))
          setValue('role', [...newRoles, 'admin']);
      } else {
        // No selections made, remove admin role
        setValue('role', currentRoles.filter(role => role !== 'admin'));
      }
    } else if (selectionModalType === 'approval-categories') {
      setSelectedApprovalCategories(selectedIds);

      // If user made selections, add approver role; if no selections, remove approver role
      if (selectedIds.length > 0) {
        const newRoles = currentRoles.filter(role => role !== 'executive-admin');
        if (!currentRoles.includes('approver'))
          setValue('role', [...newRoles, 'approver']);
      } else {
        // No selections made, remove approver role
        setValue('role', currentRoles.filter(role => role !== 'approver'));
      }
    }

    setShowSelectionContent(false);
    setSelectionModalType(null);
  };

  const handleOpenSelectionModal = (type: SelectionModalType, event?: React.MouseEvent) => {
    if (event)
      event.stopPropagation(); // Prevent box click when clicking chevron

    setSelectionModalType(type);
    setShowSelectionContent(true);
  };

  const handleSelectionCancel = () => {
    setShowSelectionContent(false);
    setSelectionModalType(null);
  };

  const handleDismiss = () => {
    const before = initialSnapshotRef.current;
    const currentRoles: string[] = watch('role') || [];
    const currentDeactivated = watch('deactivated');
    const hadChanges = Boolean(
      before &&
        (currentDeactivated !== before.deactivated ||
          currentRoles.length !== before.roles.length ||
          currentRoles.some(role => !before.roles.includes(role))),
    );
    onDismissed?.({ hadChanges });
    onClose?.();
  };

  return (
    <>
      <Modal
        title={
          showSelectionContent
            ? selectionModalType === 'organizations'
              ? 'Assign Organizations'
              : 'Assign Approval Category'
            : `Update ${rUser.data?.firstName ?? ''} ${
                rUser.data?.lastName ?? ''
              }'s Permissions`
        }
        open={open}
        onClose={handleDismiss}
        headerStyle={{ justifyContent: 'start' }}
        contentStyle={{ paddingTop: showSelectionContent ? 0 : 24 }}
      >
        {showSelectionContent ? (
          <SelectionContent
            modalType={selectionModalType}
            onApply={handleSelectionApply}
            onCancel={handleSelectionCancel}
            selectedIds={
              selectionModalType === 'organizations'
                ? selectedOrganizations
                : selectionModalType === 'approval-categories'
                ? selectedApprovalCategories
                : []
            }
            categories={eventSetupData?.categories}
            organizations={organizations}
            isLoading={isOrgsLoading}
          />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ width: 600, maxWidth: '80vw' }}>
            <Box width={300}>
              {rUser.data?.role.includes(RoleMap.master) ? (
                <Select label="Role" disabled>
                  <option>Master</option>
                </Select>
              ) : (
                <Box>
                  <Typography sx={{ fontWeight: 600, marginBottom: 10 }}>
                    Roles
                  </Typography>
                  <Stack spacing={10}>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() => handleBoxClick('executive-admin')}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f8f8f8' },
                        padding: '8px',
                        borderRadius: '10px',
                        border: '1px solid #DEDEDE',
                      }}
                    >
                      <Controller
                        control={control}
                        name="role"
                        render={({ field: { value = [] } }) => (
                          <Checkbox
                            checked={value.includes('executive-admin')}
                            onChange={e =>
                              handleRoleChange('executive-admin', e.target.checked)
                            }
                          />
                        )}
                      />
                      <Typography variant="body2" component="span">
                        Executive Admin
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() => handleBoxClick('admin')}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f8f8f8' },
                        padding: '8px',
                        borderRadius: '10px',
                        border: '1px solid #DEDEDE',
                      }}
                    >
                      <Controller
                        control={control}
                        name="role"
                        render={({ field: { value = [] } }) => (
                          <Checkbox
                            checked={value.includes('admin')}
                            onChange={e => {
                              handleRoleChange('admin', e.target.checked);
                            }}
                            disabled={isRoleDisabled('admin')}
                          />
                        )}
                      />
                      <Typography
                        variant="body2"
                        component="span"
                        sx={{ flex: 1 }}
                      >
                        Admin
                      </Typography>
                      {currentRoles.includes('admin') &&
                        selectedOrganizations.length > 0 && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 8 }}
                          >
                            {selectedOrganizations.length} Organization(s)
                          </Typography>
                        )}
                      {currentRoles.includes('admin') && (
                        <ChevronRight
                          onClick={e => {
                            if (currentRoles.includes('admin'))
                              handleOpenSelectionModal('organizations', e);
                          }}
                          fontSize="medium"
                          color="action"
                          sx={{
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#24305f',
                              color: '#ffffff',
                              transform: 'scale(1.1)',
                            },
                          }}
                        />
                      )}
                    </Box>
                    {isMultiStepApprovalEnabled && (
                      <Box
                        display="flex"
                        alignItems="center"
                        onClick={() => handleBoxClick('approver')}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#f8f8f8' },
                          padding: '8px',
                          borderRadius: '10px',
                          border: '1px solid #DEDEDE',
                        }}
                      >
                        <Controller
                          control={control}
                          name="role"
                          render={({ field: { value = [] } }) => (
                            <Checkbox
                              checked={value.includes('approver')}
                              onChange={e => {
                                handleRoleChange('approver', e.target.checked);
                              }}
                              disabled={isRoleDisabled('approver')}
                            />
                          )}
                        />
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ flex: 1 }}
                        >
                          Approver
                        </Typography>
                        {currentRoles.includes('approver') &&
                          selectedApprovalCategories.length > 0 && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mr: 8 }}
                            >
                              {selectedApprovalCategories.length} Category(s)
                            </Typography>
                          )}
                        {currentRoles.includes('approver') && (
                          <ChevronRight
                            onClick={e => {
                              if (currentRoles.includes('approver'))
                                handleOpenSelectionModal('approval-categories', e);
                            }}
                            fontSize="medium"
                            color="action"
                            sx={{
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                backgroundColor: '#24305f',
                                color: '#ffffff',
                                transform: 'scale(1.1)',
                              },
                            }}
                          />
                        )}
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>

            <Box mt={30} mb={10}>
              <Controller
                control={control}
                name="deactivated"
                render={({ field: { value, onChange } }) => (
                  <Box display="flex" alignItems="center">
                    <Typography sx={{ fontWeight: 600 }}>
                      Deactivate Account?
                    </Typography>
                    <Box mx={10}>
                      <Switch on={value} onToggle={() => onChange(!value)} />
                    </Box>
                    {value && (
                      <Typography color="error" style={{ fontWeight: 600 }}>
                        Account Deactivated
                      </Typography>
                    )}
                  </Box>
                )}
              />
            </Box>
          </Box>

          <Box mt={30} display="flex">
            <Box mr={30}>
              <Button minWidth={100} inverted onClick={handleDismiss}>
                Cancel
              </Button>
            </Box>
            <Button
              minWidth={100}
              type="submit"
              variant="vibrant"
              disabled={isSubmitting}
            >
              Save
            </Button>
          </Box>
        </form>
        )}
      </Modal>
    </>
  );
};
